/**
 * Altair Agent Component (FAQ-only)
 *
 * Multi-agent voice flow: greeting -> welcome -> faq.
 * Tool handlers (set_language_preference, proceed_to_faq, search_faq, back_to_menu) live in agent nodes.
 */

import { useEffect, memo } from 'react';
import { useAgentContext } from '../../contexts/AgentContext';
import { useTranscript } from '../../contexts/TranscriptContext';
import { AllPermissions } from '../../utils/permissions';
import { logger } from '../../utils/logger';
import { getEmbeddedHandler } from '../../services/embedded-handler';

export type LivechatLanguage = 'ar' | 'en' | 'ur';

interface AltairAgentProps {
  permissions?: AllPermissions | null;
  userData?: { username: string; mobileNumber: string } | null;
  voiceGender?: 'male' | 'female';
  /** When set (e.g. from in-app settings), used in setConfig so connect starts with this language */
  language?: LivechatLanguage;
  onLogout?: () => void;
  videoStream?: MediaStream | null;
  embeddedMode?: boolean;
  initialAgent?: string;
  initialContext?: Record<string, any>;
  onFlowComplete?: (result: any) => void;
}

function AltairAgentComponent({
  userData,
  voiceGender = 'male',
  language: languageProp,
  embeddedMode = false,
  initialAgent,
  initialContext,
  onFlowComplete,
  setConfig,
  connect,
  updateTransitionData,
  transitionTo,
  connected,
  currentAgentId,
  context,
  isTransitioning,
  client,
  manager,
}: AltairAgentProps & {
  setConfig: (config: any) => void;
  connect: () => Promise<void>;
  updateTransitionData: (data: any) => void;
  transitionTo: (agentId: string, trigger: string, data?: any) => Promise<boolean>;
  connected: boolean;
  currentAgentId: string | null;
  context: any;
  isTransitioning: boolean;
  client: any;
  manager: any;
}) {
  const { addMessage } = useTranscript();
  const embeddedHandler = embeddedMode ? getEmbeddedHandler(true) : null;
  const detectedLanguage = context?.transitionData?.language || 'en';

  useEffect(() => {
    const configData: any = { userData, voiceGender, userLocation: undefined };
    if (initialContext) {
      logger.info('[AltairAgent] Merging initial context for deep linking:', initialContext);
      Object.assign(configData, initialContext);
    }
    if (languageProp != null) {
      configData.language = languageProp;
    } else if (initialContext?.language != null || initialContext?.contextLanguage != null) {
      configData.language = initialContext?.language ?? initialContext?.contextLanguage;
    }
    if (initialAgent != null) {
      configData.initialAgentId = initialAgent;
    } else if (configData.language != null) {
      configData.initialAgentId = 'faq_agent';
    }
    if (userData) {
      logger.connection('[AltairAgent] Setting config with user:', userData.username);
    }
    setConfig(configData);
    // When embedded or iframed, auto-start the session (no start button)
    if (embeddedMode) {
      connect().catch((err: unknown) => logger.error('[AltairAgent] Auto-connect failed:', err));
    }
  }, [userData, voiceGender, languageProp, setConfig, initialContext, initialAgent, embeddedMode, connect]);

  useEffect(() => {
    if (!embeddedHandler) return;
    embeddedHandler.onLaunch((agent: string, ctx?: Record<string, any>) => {
      logger.info('[AltairAgent] Embedded launch request:', { agent, context: ctx });
      if (ctx) updateTransitionData(ctx);
      if (connected) transitionTo(agent, 'embedded_launch', ctx);
    });
    embeddedHandler.onContextUpdate((ctx: Record<string, any>) => {
      logger.info('[AltairAgent] Embedded context update:', ctx);
      updateTransitionData(ctx);
    });
    embeddedHandler.onClose(() => logger.info('[AltairAgent] Embedded close request'));
  }, [embeddedHandler, connected, transitionTo, updateTransitionData]);

  useEffect(() => {
    if (!manager || !embeddedHandler) return;
    const handleFlowComplete = (flowType: string, result: any, agentId: string) => {
      logger.info('[AltairAgent] Flow completed:', { flowType, result, agentId });
      embeddedHandler.sendResult(result, agentId);
      if (onFlowComplete) onFlowComplete(result);
    };
    manager.on('flowCompleted', handleFlowComplete);
    return () => manager.off('flowCompleted', handleFlowComplete);
  }, [manager, embeddedHandler, onFlowComplete]);

  useEffect(() => {
    if (connected && initialAgent && initialAgent !== 'greeting_agent' && currentAgentId === 'greeting_agent') {
      logger.info(`[AltairAgent] Deep linking to ${initialAgent} after connection`);
      const timer = setTimeout(() => transitionTo(initialAgent, 'deep_link', initialContext), 500);
      return () => clearTimeout(timer);
    }
  }, [connected, initialAgent, currentAgentId, initialContext, transitionTo]);

  useEffect(() => {
    if (!embeddedHandler) return;
    const status: 'idle' | 'listening' | 'speaking' = connected && !isTransitioning ? 'idle' : 'idle';
    embeddedHandler.sendStatus(status, currentAgentId || undefined);
  }, [embeddedHandler, connected, isTransitioning, currentAgentId]);

  useEffect(() => {
    if (!client) return;
    let userTranscriptBuffer = '';
    let aiTranscriptBuffer = '';
    let userFlushTimer: number | null = null;
    let aiFlushTimer: number | null = null;

    const flushUserBuffer = () => {
      if (userTranscriptBuffer.trim()) {
        addMessage({ role: 'user', content: userTranscriptBuffer.trim() });
        userTranscriptBuffer = '';
      }
    };
    const flushAIBuffer = () => {
      if (aiTranscriptBuffer.trim()) {
        addMessage({ role: 'assistant', content: aiTranscriptBuffer.trim(), agentId: currentAgentId || undefined });
        aiTranscriptBuffer = '';
      }
    };

    const handleUserTranscription = (text: string) => {
      userTranscriptBuffer = (userTranscriptBuffer ? userTranscriptBuffer + ' ' : '') + text;
      if (userFlushTimer) clearTimeout(userFlushTimer);
      userFlushTimer = window.setTimeout(() => { flushUserBuffer(); userFlushTimer = null; }, 800);
    };
    const handleAITranscription = (text: string) => {
      if (userTranscriptBuffer.trim()) {
        if (userFlushTimer) { clearTimeout(userFlushTimer); userFlushTimer = null; }
        flushUserBuffer();
      }
      aiTranscriptBuffer = (aiTranscriptBuffer ? aiTranscriptBuffer + ' ' : '') + text;
      if (aiFlushTimer) clearTimeout(aiFlushTimer);
      aiFlushTimer = window.setTimeout(() => { flushAIBuffer(); aiFlushTimer = null; }, 800);
    };
    const handleTurnComplete = () => {
      if (userFlushTimer) { clearTimeout(userFlushTimer); userFlushTimer = null; }
      if (aiFlushTimer) { clearTimeout(aiFlushTimer); aiFlushTimer = null; }
      flushUserBuffer();
      flushAIBuffer();
    };

    client.on('inputTranscription', handleUserTranscription);
    client.on('outputTranscription', handleAITranscription);
    client.on('turncomplete', handleTurnComplete);
    return () => {
      if (userFlushTimer) clearTimeout(userFlushTimer);
      if (aiFlushTimer) clearTimeout(aiFlushTimer);
      client.off('inputTranscription', handleUserTranscription);
      client.off('outputTranscription', handleAITranscription);
      client.off('turncomplete', handleTurnComplete);
    };
  }, [client, addMessage, currentAgentId]);

  return (
    <div className="altair-agent">
      {process.env.NODE_ENV === 'development' && (
        <div style={{
          position: 'fixed',
          top: 10,
          right: 10,
          padding: 10,
          backgroundColor: 'rgba(0,0,0,0.8)',
          color: 'white',
          fontSize: 12,
          borderRadius: 4,
          zIndex: 9999,
        }}>
          <div>Agent: {currentAgentId || 'none'}</div>
          <div>Connected: {connected ? '✓' : '✗'}</div>
          <div>Language: {detectedLanguage}</div>
          <div>Transitioning: {isTransitioning ? '...' : 'no'}</div>
        </div>
      )}
    </div>
  );
}

function AltairAgentWithContext(props: AltairAgentProps) {
  const {
    setConfig,
    connect,
    updateTransitionData,
    transitionTo,
    connected,
    currentAgentId,
    context,
    isTransitioning,
    client,
    manager,
  } = useAgentContext();
  return (
    <AltairAgentComponent
      {...props}
      setConfig={setConfig}
      connect={connect}
      updateTransitionData={updateTransitionData}
      transitionTo={transitionTo}
      connected={connected}
      currentAgentId={currentAgentId}
      context={context}
      isTransitioning={isTransitioning}
      client={client}
      manager={manager}
    />
  );
}

export const AltairAgent = memo(AltairAgentWithContext);
export default AltairAgent;
